// web/src/i18n/LanguageProvider.tsx
//
// Resolution order (spec §5):
//   1. me.preferred_language (authenticated, prop)
//   2. localStorage["language"] (cold-boot)
//   3. navigator.language.split("-")[0] (browser default)
//   4. "en"
//
// After resolution we clamp the candidate against the operator-curated
// supported set returned by GET /api/v1/languages (M9). If the candidate
// is unsupported (e.g. a stale localStorage value for a language the
// operator has since dropped from the catalog) we settle on EN and
// overwrite localStorage with the corrected value so the next cold boot
// doesn't loop. The clamp is best-effort — when /api/v1/languages is
// unreachable we keep the resolved candidate so cold boots still render.
//
// Side effects on language change:
//   - i18n.changeLanguage(code)
//   - localStorage["language"] = code
//   - <html lang> + <html dir> updated via Helmet
//
// The provider intentionally does NOT call PATCH /api/v1/me here —
// that's the responsibility of LanguageSelect (Task F10) which knows
// the user is authenticated. This keeps the provider usable on the
// signin/signup pages (pre-auth) without a 401 loop.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import i18n from "./index";
import { isRTL } from "./rtl-langs";
import { LanguageContext, type LanguageContextValue } from "./useLanguage";
import { languagesApi } from "@/api/languages";

const STORAGE_KEY = "language";
const DEFAULT_LANG = "en";

function resolveInitial(mePreferred?: string | null): string {
  if (mePreferred && /^[a-z]{2}$/.test(mePreferred)) return mePreferred;
  const stored =
    typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
  if (stored && /^[a-z]{2}$/.test(stored)) return stored;
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  const head = nav.split("-")[0]?.toLowerCase();
  if (head && /^[a-z]{2}$/.test(head)) return head;
  return DEFAULT_LANG;
}

export function LanguageProvider({
  children,
  mePreferredLanguage,
}: {
  children: ReactNode;
  mePreferredLanguage?: string | null;
}) {
  const [language, setLanguageState] = useState<string>(() =>
    resolveInitial(mePreferredLanguage),
  );

  // M9: fetch the operator-curated supported set so we can clamp stale
  // localStorage values (e.g. operator-removed "ja"). Cached by
  // React Query — the same query key is used by LanguageSelect, so the
  // fetch is shared. We don't await this in initial state because the
  // provider must render synchronously; the clamp effect below applies
  // it when the data arrives.
  const { data: languagesData } = useQuery({
    queryKey: ["languages"],
    queryFn: () => languagesApi.list(),
    // Stale-time tuned to be permissive: this list changes rarely
    // (operator action). The default 5s is fine but we set 5min to
    // avoid repeated fetches across mounts in the same session.
    staleTime: 5 * 60 * 1000,
  });

  const changeLanguage = useCallback(async (code: string) => {
    if (!/^[a-z]{2}$/.test(code)) return;
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // private mode / quota — non-fatal
    }
    setLanguageState(code);
    // Fire-and-forget — i18next backend network failures must not block the
    // language change. The provider state is the source of truth; i18n
    // catches up asynchronously and falls back to keys on missing namespaces.
    if (i18n.isInitialized) {
      void i18n.changeLanguage(code);
    }
  }, []);

  // Re-resolve if the authenticated `me` arrives after first paint.
  useEffect(() => {
    if (mePreferredLanguage && mePreferredLanguage !== language) {
      void changeLanguage(mePreferredLanguage);
    }
    // language intentionally NOT a dep: this hook reacts only to me arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mePreferredLanguage, changeLanguage]);

  // M9 clamp — once the supported list arrives, validate the current
  // candidate against it. If unsupported, fall back to EN and overwrite
  // localStorage with the corrected value so the next cold boot doesn't
  // re-resolve to the same stale entry.
  useEffect(() => {
    if (!languagesData?.languages) return;
    const supported = new Set(languagesData.languages.map((l) => l.code));
    if (supported.size === 0) return; // empty payload — keep candidate
    if (!supported.has(language)) {
      // Prefer EN if available, otherwise the first supported language.
      const fallback = supported.has(DEFAULT_LANG)
        ? DEFAULT_LANG
        : (languagesData.languages[0]?.code ?? DEFAULT_LANG);
      void changeLanguage(fallback);
    }
  }, [languagesData, language, changeLanguage]);

  // Keep i18next in sync with our state.
  useEffect(() => {
    if (i18n.isInitialized && i18n.language !== language) {
      void i18n.changeLanguage(language);
    }
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({ language, rtl: isRTL(language), setLanguage: changeLanguage }),
    [language, changeLanguage],
  );

  return (
    <LanguageContext.Provider value={value}>
      <Helmet htmlAttributes={{ lang: language, dir: isRTL(language) ? "rtl" : "ltr" }} />
      {children}
    </LanguageContext.Provider>
  );
}
